import {
  LayoutDashboard, Calendar, ClipboardList, Users, Building2, Dumbbell,
  Tag, Salad, Video, BookOpen, Bell, Trophy, Receipt, Link2, Coins,
  Brain, ShieldCheck, Home, CalendarPlus, MessageSquare, FileText, Watch,
  DoorOpen, Activity, HeartPulse, MessageCircle, Library, LineChart, ListChecks, UserMinus,
  LifeBuoy,
} from 'lucide-react';

/*
 * A navegação do GyMag numa só tabela (menu lateral do sócio, menu lateral do administrador e o título da página no cabeçalho).
 *
 * Duas regras, para o menu não mentir:
 *  - `feature` = o módulo do ginásio de que a entrada depende (Definições → Módulos). Módulo desligado = a entrada não existe, nem no menu nem na rota.
 *  - `anyFeature` = basta um destes módulos (o Progresso existe com o Treino ou com as medições do sócio).
 *  - `unlessFeature` = o contrário: a entrada desaparece quando o módulo está LIGADO (a Operação só faz falta a quem não tem Receção).
 *  - `permission` = a permissão do perfil do utilizador (um Admin tem todas).
 * Uma entrada sem nenhuma das duas está sempre à vista.
 *
 * O menu do sócio está organizado por PERGUNTA, não por tecnologia:
 *  Dashboard · Reservas (marcar, ver o que marquei, aulas online) · Treino (o que fazer hoje, o plano, o histórico; o treinador pessoal; o assistente de IA; a alimentação)
 *  · Evolução (progresso, conquistas, relógio) · Conta (faturas, créditos, mensagens, avisos).
 */

export const CLIENT_SECTIONS = [
  {
    title: null,
    items: [
      { Icon: Home, label: 'Dashboard', path: '/', match: ['/', '/home'] },
    ],
  },
  {
    // O treino vem primeiro: é a razão pela qual o atleta abre a app. As reservas são a seguir.
    title: 'Treino',
    items: [
      { Icon: Dumbbell, label: 'Treino', path: '/meu-treino', permission: 'canAccessWorkoutPlans', badge: 'workout', feature: 'TrainingPlans', match: ['/meu-treino', '/meu-treino/plano', '/meu-treino/historico', '/meu-treino/exercicios'] },
      { Icon: HeartPulse, label: 'Personal Training', path: '/pt/sessions', feature: 'PersonalTraining' },
      { Icon: Brain, label: 'Assistente de IA', path: '/ai-trainer', permission: 'canAccessAITrainer', feature: 'AiTrainer' },
      { Icon: Salad, label: 'Plano alimentar', path: '/meal-plan', permission: 'canAccessMealPlans', badge: 'mealplan', feature: 'MealPlans' },
    ],
  },
  {
    title: 'Reservas',
    items: [
      { Icon: CalendarPlus, label: 'Reservar', path: '/reservas', permission: 'canAccessBookings', feature: 'Reservations' },
      { Icon: ClipboardList, label: 'Minhas reservas', path: '/reservas/minhas', permission: 'canAccessBookings', badge: 'mybookings', feature: 'Reservations' },
      { Icon: Video, label: 'Aulas online', path: '/classes', permission: 'canAccessClasses', feature: 'OnlineClasses' },
    ],
  },
  {
    // Ferramentas de TREINADOR na área do atleta. Um Admin já tem tudo isto no painel de administração (e o
    // interruptor «Painel de administração» no menu do utilizador): mostrar-lhe estas entradas aqui era mandá-lo
    // para dentro da administração a partir da área do atleta — o dono já disse que não quer isso. `unlessAdmin`.
    title: 'Treinador',
    items: [
      { Icon: ListChecks, label: 'Planos dos atletas', path: '/admin/workout-plans-studio', permission: 'canManageWorkoutPlans', feature: 'TrainingPlans', unlessAdmin: true },
      { Icon: Library, label: 'Gerir biblioteca', path: '/admin/exercise-library', permission: 'canManageExerciseLibrary', unlessAdmin: true },
    ],
  },
  {
    title: 'Evolução',
    items: [
      { Icon: LineChart, label: 'Progresso', path: '/progress', anyFeature: ['TrainingPlans', 'ClientProgress'] },
      { Icon: Trophy, label: 'Conquistas', path: '/gamification', permission: 'canAccessGamification', feature: 'Gamification' },
      { Icon: Watch, label: 'Relógio e saúde', path: '/smartwatch', permission: 'canAccessSmartwatch', feature: 'Wearables' },
    ],
  },
  {
    title: 'Conta',
    items: [
      { Icon: FileText, label: 'Faturas e recibos', path: '/billing', permission: 'canAccessCredits', badge: 'billing', feature: 'Billing' },
      { Icon: Coins, label: 'Créditos e tokens', path: '/credits', permission: 'canAccessCredits' },
      { Icon: MessageSquare, label: 'Mensagens', path: '/messages', permission: 'canAccessMessages', badge: 'messages', feature: 'Messaging' },
      { Icon: Bell, label: 'Notificações', path: '/notifications', permission: 'canAccessNotifications', badge: 'notifications', feature: 'Notifications' },
    ],
  },
];

export const ADMIN_SECTIONS = [
  {
    title: 'Operação',
    items: [
      { Icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
      // A Operação é o ecrã de recurso para ginásios SEM o módulo Receção (quando o têm, os dois mostram o
      // mesmo e a Operação encaminha para a Receção): `unlessFeature` evita ter os dois no menu ao mesmo tempo.
      { Icon: Activity, label: 'Operação', path: '/admin/operations', unlessFeature: 'Reception' },
      { Icon: DoorOpen, label: 'Receção', path: '/admin/reception', feature: 'Reception' },
    ],
  },
  {
    title: 'Horários e aulas',
    items: [
      { Icon: Calendar, label: 'Horários', path: '/admin/legacy-dashboard?section=timeslots', feature: 'Reservations' },
      { Icon: ClipboardList, label: 'Reservas', path: '/admin/legacy-dashboard?section=bookings', feature: 'Reservations' },
      { Icon: Video, label: 'Aulas online', path: '/admin/legacy-dashboard?section=classes', feature: 'OnlineClasses' },
      { Icon: BookOpen, label: 'Tipos de aulas', path: '/admin/legacy-dashboard?section=services', feature: 'Reservations' },
    ],
  },
  {
    title: 'Pessoas',
    items: [
      { Icon: Users, label: 'Utilizadores', path: '/admin/users' },
      { Icon: UserMinus, label: 'Sócios inativos', path: '/admin/inactive-members' },
      { Icon: ShieldCheck, label: 'Gestão de perfis', path: '/admin/roles' },
      { Icon: HeartPulse, label: 'CRM · Retenção', path: '/admin/retention', feature: 'Retention' },
    ],
  },
  {
    title: 'Treino',
    items: [
      { Icon: ListChecks, label: 'Planos de treino', path: '/admin/workout-plans-studio', feature: 'TrainingPlans' },
      { Icon: Library, label: 'Biblioteca de exercícios', path: '/admin/exercise-library' },
      { Icon: Tag, label: 'Taxonomia de exercícios', path: '/admin/exercise-taxonomy' },
      { Icon: Dumbbell, label: 'Personal Training', path: '/admin/pt', feature: 'PersonalTraining' },
      { Icon: Salad, label: 'Planos alimentares', path: '/admin/meal-plans', feature: 'MealPlans' },
      { Icon: Trophy, label: 'Gamificação', path: '/admin/gamification', feature: 'Gamification' },
    ],
  },
  {
    title: 'Negócio',
    items: [
      { Icon: Receipt, label: 'Faturação', path: '/admin/billing', feature: 'Billing' },
      { Icon: Link2, label: 'Integração ERP', path: '/admin/erp' },
      { Icon: Building2, label: 'Fornecedores', path: '/admin/suppliers' },
      { Icon: Coins, label: 'Créditos e tokens', path: '/admin/token-shop' },
    ],
  },
  {
    title: 'Comunicação',
    items: [
      { Icon: MessageCircle, label: 'WhatsApp', path: '/admin/settings-basic' },
      { Icon: Bell, label: 'Notificações', path: '/admin/legacy-dashboard?section=notifications', feature: 'Notifications' },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { Icon: Brain, label: 'Assistente de IA', path: '/admin/ai-trainer', feature: 'AiTrainer' },
      // Pedidos de suporte: o registo do que o ginásio pediu ao operador da plataforma.
      { Icon: LifeBuoy, label: 'Suporte', path: '/admin/support' },
    ],
  },
];

/**
 * Só o que este utilizador pode ver neste ginásio. `isEnabled(feature)` vem de useCapabilities (desconhecido
 * = falso: nunca mostra um módulo por engano). Uma secção que fica sem entradas desaparece.
 *
 * `isAdmin` serve para não repetir no menu do atleta o que o Admin já tem no painel de administração
 * (`unlessAdmin`): a área do atleta não manda ninguém para dentro da administração.
 */
export function visibleSections(sections, { hasPermission, isEnabled, isAdmin = false }) {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (!item.permission || hasPermission(item.permission)) && (!item.feature || isEnabled(item.feature)) && (!item.anyFeature || item.anyFeature.some((f) => isEnabled(f))) && (!item.unlessFeature || !isEnabled(item.unlessFeature)) && (!item.unlessAdmin || !isAdmin)),
    }))
    .filter((section) => section.items.length > 0);
}

/** A entrada está ativa para este endereço? (um caminho com `?` compara também a pesquisa; `match` lista os endereços que contam para a mesma entrada) */
export function isNavActive(item, pathname, search = '') {
  const here = pathname + search;
  const candidates = item.match || [item.path];
  return candidates.some((candidate) => (candidate.includes('?') ? here === candidate : pathname === candidate && !search));
}

// O título que o cabeçalho mostra: o nome da página, não "Olá, <nome>" em todas.
const TITLES = [
  ['/admin/legacy-dashboard', 'Gestão do ginásio'],
  ['/admin/exercise-library', 'Biblioteca de exercícios'],
  ['/admin/workout-plans-studio', 'Planos de treino'],
  ['/reservas/minhas', 'Minhas reservas'],
  ['/reservas', 'Reservar'],
  ['/meu-treino/plano', 'Treino'],
  ['/meu-treino/historico', 'Treino'],
  ['/meu-treino', 'Treino'],
  ['/pt/sessions', 'Personal Training'],
  ['/ai-trainer', 'Assistente de IA'],
  ['/meal-plan', 'Plano alimentar'],
  ['/progress', 'Progresso'],
  ['/gamification', 'Conquistas'],
  ['/smartwatch', 'Relógio e saúde'],
  ['/billing', 'Faturas e recibos'],
  ['/credits', 'Créditos e tokens'],
  ['/token-shop', 'Loja de tokens'],
  ['/messages', 'Mensagens'],
  ['/notifications', 'Notificações'],
  ['/classes', 'Aulas online'],
  ['/profile', 'O meu perfil'],
  ['/buddy', 'Parceiros de treino'],
  ['/checkin', 'Check-in'],
];

// Títulos das páginas de administração: o nome que o menu lhes dá. Antes qualquer página sob /admin dizia «Dashboard»
// (a regra '/admin' apanhava tudo), por isso a Receção, os Utilizadores, as Definições… tinham o título errado.
const ADMIN_EXTRA_TITLES = [
  ['/admin/inactive-members', 'Sócios inativos'],
  ['/admin/members', 'Ficha do membro'],
  ['/admin/settings', 'Definições'],
  ['/admin/analytics-pro', 'Análises'],
  ['/admin/analytics', 'Análises'],
  ['/analytics-pro', 'Análises'],
  ['/analytics', 'Análises'],
  ['/admin/ai-dashboard', 'Assistente de IA'],
  ['/admin/ai-trainer', 'Assistente de IA'],
  ['/admin/ai', 'Assistente de IA'],
  ['/admin/consultor-financeiro', 'Consultor financeiro'],
  ['/admin/billing', 'Faturação'],
  ['/users', 'Utilizadores'],
];

const ADMIN_MENU_TITLES = ADMIN_SECTIONS.flatMap((s) => s.items)
  .filter((i) => !i.path.includes('?') && i.path !== '/admin')
  .map((i) => [i.path, i.label]);

export function titleForPath(pathname) {
  if (pathname === '/' || pathname === '/home' || pathname === '/admin') return 'Dashboard';
  // O prefixo mais específico ganha (o item «Dashboard» do menu é '/admin' e apanharia tudo o que está por baixo).
  const hit = [...TITLES, ...ADMIN_EXTRA_TITLES, ...ADMIN_MENU_TITLES]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/'));
  if (hit) return hit[1];
  return pathname.startsWith('/admin/') ? 'Administração' : null;
}
